import { useCallback, useRef, useEffect, useMemo } from 'react'
import { cn } from '@/true-fsd/shared/utils/css'
import { MIN_FONT_SIZE, MAX_FONT_SIZE } from '../constants'
import classes from './FontSizeSlider.module.scss'

/**
 * Пропсы для компонента FontSizeSlider
 */
export interface FontSizeSliderProps {
    /**
     * Текущее значение размера шрифта (4-18px)
     */
    value: number

    /**
     * Обработчик изменения размера шрифта
     */
    onChange: (value: number) => void

    /**
     * Минимальное значение (по умолчанию 4)
     */
    min?: number

    /**
     * Максимальное значение (по умолчанию 18)
     */
    max?: number

    /**
     * Дополнительные CSS классы
     */
    className?: string

    /**
     * Отключен ли слайдер
     */
    disabled?: boolean
}

/**
 * Компонент ползунка для изменения размера шрифта маркировки
 * Диапазон: 4-18px
 */
export function FontSizeSlider({
    value,
    onChange,
    min = MIN_FONT_SIZE,
    max = MAX_FONT_SIZE,
    className,
    disabled = false,
}: FontSizeSliderProps) {
    const sliderRef = useRef<HTMLInputElement>(null)

    // Вычисляем размер thumb на основе текущего значения
    // value = 4px (низ) -> thumb = 6px
    // value = 18px (верх) -> thumb = 10px
    const minThumbSize = 6 // Минимальный размер thumb при value = 4px
    const maxThumbSize = 12 // Максимальный размер thumb при value = 18px

    // Линейная интерполяция размера thumb
    const thumbSize = useMemo(() => {
        const ratio = (value - min) / (max - min) // 0 при value = min, 1 при value = max
        return minThumbSize + (maxThumbSize - minThumbSize) * ratio
    }, [value, min, max])

    /**
     * Обработчик изменения значения ползунка
     */
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = Number.parseInt(e.target.value, 10)
            if (!Number.isNaN(newValue)) {
                onChange(newValue)
            }
        },
        [onChange]
    )

    /**
     * Обработчик для обновления в реальном времени при перетаскивании
     */
    const handleInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = Number.parseInt(e.target.value, 10)
            if (!Number.isNaN(newValue)) {
                onChange(newValue)
                // Стили обновятся автоматически через useEffect при изменении value
            }
        },
        [onChange]
    )

    /**
     * Обработчик прокрутки колесиком мышки для изменения размера шрифта
     */
    const handleWheel = useCallback(
        (e: React.WheelEvent<HTMLInputElement>) => {
            if (disabled) {
                return
            }

            // Предотвращаем стандартную прокрутку страницы
            e.preventDefault()
            e.stopPropagation()

            // Определяем направление прокрутки
            // deltaY > 0 - прокрутка вниз (уменьшение)
            // deltaY < 0 - прокрутка вверх (увеличение)
            const delta = e.deltaY

            // Определяем шаг изменения
            // С зажатым Shift - больший шаг (2px), без Shift - обычный шаг (1px)
            const step = e.shiftKey ? 2 : 1

            // Вычисляем новое значение
            let newValue = value
            if (delta > 0) {
                // Прокрутка вниз - уменьшаем
                newValue = Math.max(min, value - step)
            } else if (delta < 0) {
                // Прокрутка вверх - увеличиваем
                newValue = Math.min(max, value + step)
            }

            // Обновляем значение только если оно изменилось
            if (newValue !== value) {
                onChange(newValue)
            }
        },
        [value, min, max, onChange, disabled]
    )

    // Принудительно применяем стили к thumb через JavaScript
    // CSS переменные не всегда работают в псевдоэлементах слайдеров
    // Поэтому используем динамические стили через JavaScript
    useEffect(() => {
        if (!sliderRef.current) return

        const slider = sliderRef.current

        // Функция для применения стилей к thumb с динамическим размером
        const applyThumbStyles = () => {
            // Удаляем старый стиль, если есть
            const oldStyle = document.getElementById(
                'font-size-slider-thumb-styles'
            )
            if (oldStyle) {
                oldStyle.remove()
            }

            const sliderId = slider.id || 'font-size-slider'
            const thumbSizePx = `${thumbSize}px`
            // Трек имеет ширину 4px и центрирован через margin-left: -2px
            // Для центрирования thumb относительно трека: смещаем влево на (thumbSize - 4) / 2
            const trackWidth = 4
            const thumbMarginLeft = `${-(thumbSize - trackWidth) / 2}px`

            // Создаем динамические стили с максимальной специфичностью
            const style = document.createElement('style')
            style.id = 'font-size-slider-thumb-styles'
            style.textContent = `
                input#${sliderId}::-webkit-slider-thumb,
                #${sliderId}::-webkit-slider-thumb {
                    width: ${thumbSizePx} !important;
                    height: ${thumbSizePx} !important;
                    min-width: ${thumbSizePx} !important;
                    min-height: ${thumbSizePx} !important;
                    max-width: ${thumbSizePx} !important;
                    max-height: ${thumbSizePx} !important;
                    margin-left: ${thumbMarginLeft} !important;
                }
                input#${sliderId}::-moz-range-thumb,
                #${sliderId}::-moz-range-thumb {
                    width: ${thumbSizePx} !important;
                    height: ${thumbSizePx} !important;
                    min-width: ${thumbSizePx} !important;
                    min-height: ${thumbSizePx} !important;
                    max-width: ${thumbSizePx} !important;
                    max-height: ${thumbSizePx} !important;
                    margin-left: ${thumbMarginLeft} !important;
                }
            `
            document.head.appendChild(style)

            // Также обновляем CSS переменные на случай, если браузер их поддерживает
            slider.style.setProperty('--thumb-size', thumbSizePx)
            slider.style.setProperty('--thumb-margin-left', thumbMarginLeft)
        }

        applyThumbStyles()

        return () => {
            const style = document.getElementById(
                'font-size-slider-thumb-styles'
            )
            if (style) {
                style.remove()
            }
        }
    }, [thumbSize])

    return (
        <div className={cn(classes.sliderContainer, className)}>
            <label className={classes.label} htmlFor="font-size-slider">
                Размер шрифта: {value}px
            </label>
            <input
                ref={sliderRef}
                id="font-size-slider"
                type="range"
                min={min}
                max={max}
                value={value}
                onChange={handleChange}
                onInput={handleInput}
                onWheel={handleWheel}
                disabled={disabled}
                className={classes.slider}
                aria-label="Размер шрифта"
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={value}
                aria-orientation="vertical"
                style={{
                    // Явно устанавливаем accent-color через inline стили для гарантии
                    accentColor: '#056fff',
                    // Передаем размер thumb через CSS переменную для использования в SCSS
                    ['--thumb-size' as string]: `${thumbSize}px`,
                    ['--thumb-margin-left' as string]: `${-(thumbSize - 4) / 2}px`,
                }}
                {...({
                    orient: 'vertical',
                } as React.InputHTMLAttributes<HTMLInputElement>)}
            />
        </div>
    )
}
